<script>
import axios from 'axios'

import { UsersService } from './users.service.vue'
import { ProjectsService } from './projects.service.vue'
import { ActivitiesService } from './activities.service.vue'
import { UserStatsService } from './userstats.service.vue'
import { AssetsService } from './assets.service.vue'
import { HomeService } from './home.service.vue'

// Pass-through. Every service module wraps its calls in
// `catch (error) { return handleError(error) }`, so this stays as the shared
// hook rather than editing eight files to remove a no-op.
//
// It used to call deleteCookies()/deleteCache() on 401/403 — except it tested
// `error.status`, and axios puts the status on `error.response.status`. The
// branch never once executed. It is deleted rather than fixed: auth failures
// are handled globally by the response interceptor in Azure.vue, and there are
// no longer any client-side auth artefacts to clear.
const handleError = (error) => {
  throw error
}

// withCredentials is now the ONLY authentication mechanism: the API reads the
// httpOnly __session cookie. No Authorization header is set anywhere — the
// browser holds no token to put in one.
const createApiInstance = (baseURL) => {
  return axios.create({
    baseURL,
    method: 'get',
    headers: {
      'Content-Type': 'application/json'
    },
    timeout: 40000,
    withCredentials: true
  })
}

const checkServiceHealth = async (api) => {
  try {
    let url = '/health'
    if (location.hash) {
      url = `${url}?code=${location.hash.split('=')[1]}`
    }
    return await api.get(url)
  } catch (error) {
    console.log({ error })
    throw new Error('Failed to check service health')
  }
}

const measureDownloadSpeed = async (api) => {
  const fileUrl = '/network-speed-check'
  const fileSizeInBytes = 83400
  const startTime = new Date().getTime()
  try {
    await api.get(fileUrl, {
      params: {
        rand: Math.random()
      },
      responseType: 'blob'
    })
    const endTime = new Date().getTime()
    const durationInSeconds = (endTime - startTime) / 1000
    if (durationInSeconds === 0) return Infinity

    const bitsLoaded = fileSizeInBytes * 8
    const speedBps = bitsLoaded / durationInSeconds
    const speedKbps = speedBps / 1024
    const speedMbps = speedKbps / 1024
    return {
      speed: {
        bps: Number(parseFloat(speedBps).toFixed(2)),
        kb: Number(parseFloat(speedKbps).toFixed(2)),
        mb: Number(parseFloat(speedMbps).toFixed(2)),
      }
    }
  } catch (error) {
    console.error('Error measuring speed:', error)
    return null
  }
}

const MainService = () => {
  const api = createApiInstance(process.env.VUE_APP_API_BASE_URL_V2)

  api.interceptors.request.use(
    (config) => config,
    (error) => Promise.reject(error)
  )

  api.interceptors.response.use(
    (response) => {
      if (response.status === 200) {
        return Promise.resolve(response)
      }
      return Promise.reject(response)
    },
    (error) => {
      // 401 is the ordinary signed-out state, not a fault: the API answers it
      // for anyone without a valid session, including first-time visitors on
      // the boot probe. Logging it filled the console on every visit. The
      // interceptor in Azure.vue turns it into the sign-in button.
      if (error?.response?.status !== 401) {
        console.log('req-exception:', error.message)
      }
      return Promise.reject(error)
    }
  )

  return {
    api,
    checkServiceHealth: () => checkServiceHealth({ ...api }),
    measureDownloadSpeed: () => measureDownloadSpeed({ ...api }),
    ReqInteceptors: api.interceptors.request,
    ResInteceptors: api.interceptors.response,
    Users: UsersService(api, handleError),
    Projects: ProjectsService(api, handleError),
    Activities: ActivitiesService(api, handleError),
    UserStats: UserStatsService(api, handleError),
    Assets: AssetsService(api, handleError),
    Home: HomeService(api, handleError)
  }
}

export default MainService()
</script>
